import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { useTheme, type ThemeTokens } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { useIsMobileViewport } from '../hooks/useIsMobileViewport';
import { createContentBlockStyles } from '../styles/contentBlocks';

type Step = 'question' | 'review' | 'confirmation';

export interface AskExpertFormProps {
  onSubmit?: (data: { question: string; email: string }) => Promise<void> | void;
  onDone?: () => void;
  isPreview?: boolean;
  learnerEmail?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function AskExpertForm({ onSubmit, onDone, isPreview = false, learnerEmail = '' }: AskExpertFormProps) {
  const { tokens } = useTheme();
  const { t } = useTranslation();
  const sharedStyles = useMemo(() => createContentBlockStyles(tokens), [tokens]);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const styles = useMemo(() => createStyles(tokens, isSmallMobile), [tokens, isSmallMobile]);

  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);
  const [textareaFocused, setTextareaFocused] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [step, setStep] = useState<Step>('question');
  const [question, setQuestion] = useState('');
  const [email, setEmail] = useState(learnerEmail);
  const [emailTouched, setEmailTouched] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const emailValid = EMAIL_REGEX.test(email);
  const showEmailError = emailTouched && email.length > 0 && !emailValid;

  const handleSubmitQuestion = useCallback(() => {
    if (question.trim()) setStep('review');
  }, [question]);

  const handleSend = useCallback(async () => {
    if (!emailValid || submitting) return;
    setSubmitting(true);
    setSubmitError(false);
    try {
      if (!isPreview) {
        await onSubmit?.({ question: question.trim(), email: email.trim() });
      }
      setStep('confirmation');
    } catch (err) {
      console.error('[AskExpert] submit failed', err);
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  }, [email, emailValid, isPreview, onSubmit, question, submitting]);

  const handleCancel = useCallback(() => {
    setEmail(learnerEmail);
    setEmailTouched(false);
    setSubmitError(false);
    setStep('question');
  }, [learnerEmail]);

  const handleDone = useCallback(() => {
    setQuestion('');
    setEmail(learnerEmail);
    setEmailTouched(false);
    setStep('question');
    onDone?.();
  }, [learnerEmail, onDone]);

  if (step === 'confirmation') {
    return (
      <div style={styles.container} role="status" aria-live="polite">
        <h3 style={styles.confirmationTitle}>{t('ask_expert_form.confirmation_title')}</h3>
        <p style={styles.confirmationBody}>{t('ask_expert_form.confirmation_body')}</p>
        {isPreview && <p style={styles.previewNotice}>{t('ask_expert_form.preview_notice')}</p>}
        <div style={styles.buttonRowCenter}>
          <button
            type="button"
            style={{
              ...sharedStyles.actionButton,
              ...(hoveredBtn === 'done' ? sharedStyles.actionButtonHover : {}),
            }}
            onMouseEnter={() => setHoveredBtn('done')}
            onMouseLeave={() => setHoveredBtn(null)}
            onClick={handleDone}
          >
            {t('ask_expert_form.got_it')}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'review') {
    return (
      <div style={styles.container}>
        <div style={styles.containerSpacing}>
        <p style={styles.text}>
          {t('ask_expert_form.review_intro')}
        </p>
        <p style={styles.disclaimer}>{t('ask_expert_form.disclaimer')}</p>
        </div>
        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="ask-expert-email">{t('ask_expert_form.label_email')}</label>
          <input
            id="ask-expert-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            style={showEmailError ? styles.inputError : styles.input}
          />
          {showEmailError && (
            <span style={styles.errorText}>{t('ask_expert_form.email_error')}</span>
          )}
        </div>
        <span style={styles.helperText}>{t('ask_expert_form.email_helper')}</span>

        {submitError && (
          <span role="alert" style={styles.errorText}>
            {t('ask_expert_form.submit_error')}
          </span>
        )}

        <div style={styles.buttonRow}>
          <button
            type="button"
            style={{
              ...sharedStyles.secondaryActionButton,
              ...(hoveredBtn === 'cancel' ? sharedStyles.secondaryActionButtonHover : {}),
            }}
            onMouseEnter={() => setHoveredBtn('cancel')}
            onMouseLeave={() => setHoveredBtn(null)}
            onClick={handleCancel}
          >
            {t('ask_expert_form.cancel')}
          </button>
          <button
            type="button"
            style={{
              ...sharedStyles.actionButton,
              ...(!emailValid || submitting ? sharedStyles.actionButtonDisabled : {}),
              ...(hoveredBtn === 'send' && emailValid && !submitting ? sharedStyles.actionButtonHover : {}),
            }}
            disabled={!emailValid || submitting}
            onMouseEnter={() => setHoveredBtn('send')}
            onMouseLeave={() => setHoveredBtn(null)}
            onClick={() => { void handleSend(); }}
          >
            {submitting ? t('ask_expert_form.sending') : t('ask_expert_form.send')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, gap: tokens.spacing['2xl'] }}>
      <div style={styles.containerHeader}>
      <p style={styles.text}>
        {t('ask_expert_form.heading')}
      </p>
      <p style={styles.subtext}>{t('ask_expert_form.subheading')}</p>
      </div>

      <div style={styles.fieldGroup}>
        <label style={styles.label} htmlFor="ask-expert-question">{t('ask_expert_form.label_question')}</label>
        <textarea
          id="ask-expert-question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onFocus={() => setTextareaFocused(true)}
          onBlur={() => setTextareaFocused(false)}
          style={{
            ...styles.textarea,
            ...(textareaFocused ? styles.textareaFocus : {}),
          }}
          rows={6}
        />
      </div>

      <div style={styles.buttonRowCenter}>
        <button
          type="button"
          style={{
            ...sharedStyles.actionButton,
            ...(!question.trim() ? sharedStyles.actionButtonDisabled : {}),
            ...(hoveredBtn === 'submit' && question.trim() ? sharedStyles.actionButtonHover : {}),
          }}
          disabled={!question.trim()}
          onMouseEnter={() => setHoveredBtn('submit')}
          onMouseLeave={() => setHoveredBtn(null)}
          onClick={handleSubmitQuestion}
        >
          {t('ask_expert_form.submit')}
        </button>
      </div>
    </div>
  );
}

function createStyles(tokens: ThemeTokens, isSmallMobile: boolean): Record<string, CSSProperties> {
  return {
    container: {
      padding: isSmallMobile ? tokens.spacing.lgPlus : tokens.spacing["2xl"],
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xl,
      overflow: 'auto',
    },
    containerHeader: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xs,
    },
    containerSpacing: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
    },
    text: {
      margin: 0,
      fontSize: tokens.typography.sizes.md,
      color: tokens.text.primary,
      lineHeight: tokens.typography.lineHeights.uiSoft,
      fontWeight: tokens.typography.weights.bold,
    },
    subtext: {
      margin: 0,
      fontSize: tokens.typography.sizes.ui,
      color: tokens.text.primary,
      lineHeight: tokens.typography.lineHeights.uiSoft,
    },
    disclaimer: {
      margin: 0,
      fontSize: tokens.typography.sizes.base,
      fontWeight: tokens.typography.weights.regular,
      color: tokens.text.muted,
      lineHeight: tokens.typography.lineHeights.uiSoft,
    },
    fieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
    },
    label: {
      fontSize: tokens.typography.sizes.ui,
      fontWeight: tokens.typography.weights.regular,
      color: tokens.text.primary,
      lineHeight: tokens.typography.lineHeights.uiSoft,
    },
    textarea: {
      width: '100%',
      minHeight: '220px',
      padding: tokens.spacing.lg,
      fontSize: tokens.typography.sizes.md,
      fontFamily: tokens.typography.fontFamily,
      border: `1px solid ${tokens.borders.interactive}`,
      borderRadius: tokens.radius.xs,
      resize: 'vertical' as const,
      outline: 'none',
      boxSizing: 'border-box' as const,
      color: tokens.text.primary,
      transition: `border-color ${tokens.ui.transitionDefault}`,
    },
    textareaFocus: {
      // NA-242: focus borders use the fixed UI accent — never the account accent.
      borderColor: tokens.ui.fixedAccent,
    },
    input: {
      width: '100%',
      height: '54px',
      padding: `0px ${tokens.spacing.lg}`,
      fontSize: tokens.typography.sizes.md,
      fontWeight: tokens.typography.weights.regular,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.primary,
      backgroundColor: tokens.backgrounds.page,
      border: `1px solid ${tokens.borders.interactive}`,
      borderRadius: tokens.radius.xs,
      outline: 'none',
      transition: tokens.ui.transitionDefault,
      boxSizing: 'border-box' as const,
    },
    inputError: {
      width: '100%',
      height: '54px',
      padding: `0px ${tokens.spacing.lg}`,
      fontSize: tokens.typography.sizes.md,
      fontFamily: tokens.typography.fontFamily,
      border: `1px solid ${tokens.semantic.error}`,
      borderRadius: tokens.radius.xs,
      outline: 'none',
      boxSizing: 'border-box' as const,
    },
    helperText: {
      fontSize: tokens.typography.sizes.base,
      fontWeight: tokens.typography.weights.regular,
      color: tokens.text.muted,
    },
    errorText: {
      fontSize: tokens.typography.sizes.base,
      color: tokens.semantic.error,
    },
    buttonRow: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    buttonRowCenter: {
      display: 'flex',
      justifyContent: 'center',
    },

    confirmationTitle: {
      margin: 0,
      fontSize: tokens.typography.sizes.md,
      fontWeight: tokens.typography.weights.bold,
      color: tokens.text.primary,
    },
    confirmationBody: {
      margin: 0,
      fontSize: tokens.typography.sizes.ui,
      fontWeight: tokens.typography.weights.regular,
      color: tokens.text.primary,
      lineHeight: tokens.typography.lineHeights.uiSoft,
    },
    previewNotice: {
      margin: 0,
      fontSize: tokens.typography.sizes.ui,
      fontWeight: tokens.typography.weights.regular,
      color: tokens.semantic.error,
      lineHeight: tokens.typography.lineHeights.uiSoft,
    },
  };
}
